import { acceptHMRUpdate, defineStore } from 'pinia';
import { getInitialEntityState } from '@/store/common/entities-state';
import { createEntityActions } from '@/store/common/entities-actions';
import { Collection } from '@/store/common/collections';
import { Location } from '@/model/location';
import { isAfter } from 'date-fns';
import { toDate } from '@/helpers/date';
import { createEntityGetters } from '@/store/common/entities-getters';
import { InputOption } from '@/model/input-option';
import { reactive } from 'vue';
import entitiesService from '@/services/entities.service';
import * as Sentry from '@sentry/vue';
import { useUiStore } from '@/store/ui-store';
import resourceService from '@/services/resource.service';

const initialEntityState = reactive({
  ...getInitialEntityState<Location>(),
  publicLocations: {
    fetching: false as boolean,
    entities: [] as Location[],
  },
});

export const useLocationsStore = defineStore('locations', {
  state: () => initialEntityState,
  getters: {
    ...createEntityGetters<Location>(),
    getBySlug: (state) => {
      return (slug: string) => {
        return state.entities.find((entity) => {
          return entity.slug === slug;
        });
      };
    },
    getLocationsAsSelectOptions(): InputOption[] {
      return this.entities.map((location) => {
        return {
          value: location.id + '',
          label: location.name,
        };
      });
    },
    getOpenLocationsAsSelectOptions() {
      return (vendorId: number | null): InputOption[] => {
        const entities = this.getOpenLocations(vendorId);
        return entities.map((location) => {
          return {
            value: location.id + '',
            label: location.name,
          };
        });
      };
    },
    getOpenLocations(state) {
      return (vendorId: number | null = null): Location[] => {
        let entities: Location[];
        if (vendorId !== null) {
          entities = state.entities;
        } else {
          entities = state.publicLocations.entities;
        }
        return entities.filter((location) => {
          if (location.closed_from === null) {
            return true;
          }
          return isAfter(toDate(location.closed_from), new Date());
        });
      };
    },
    getLocationsWithoutRegion(): Location[] {
      return this.entities.filter((location) => {
        return location.region_id === null;
      });
    },
  },
  actions: {
    ...createEntityActions<Location>(Collection.LOCATIONS, initialEntityState),
    async findAllPublic() {
      try {
        this.publicLocations.fetching = true;
        this.publicLocations.entities = await entitiesService.findAll(
          Collection.LOCATIONS,
          null,
        );
        this.publicLocations.fetching = false;
      } catch (exception) {
        console.error(exception);
        this.publicLocations.fetching = false;
        const uiStore = useUiStore();
        uiStore.alert(exception.message);
        Sentry.captureException(exception);
      }
    },
    getLocationsForRegionId(regionId: number): Location[] {
      return this.entities.filter((location) => {
        return location.region_id === regionId;
      });
    },
    async verifyClose(locationId: number, closedFrom: string) {
      this.validationErrors = {};

      return resourceService.verifyClosedFrom(locationId, closedFrom);
    },
    async saveClose(locationId: number, closedFrom: string) {
      this.validationErrors = {};

      return this.applyClosedFrom(locationId, closedFrom, () => {
        return resourceService.closedFrom(locationId, closedFrom);
      });
    },
    async reopenLocation(locationId: number) {
      return this.applyClosedFrom(locationId, null, () => {
        return resourceService.reopenLocation(locationId);
      });
    },
    async applyClosedFrom(
      locationId: number,
      closedFrom: string | null,
      request: () => Promise<unknown>,
    ) {
      this.saving = true;

      try {
        const response = await request();

        if (this.currentEntity?.id === locationId) {
          this.currentEntity = {
            ...this.currentEntity,
            closed_from: closedFrom,
          };
        }

        const index = this.entities.findIndex((loc) => loc.id === locationId);
        if (index !== -1) {
          this.entities[index] = {
            ...this.entities[index],
            closed_from: closedFrom,
          };
        }

        return response;
      } finally {
        this.saving = false;
      }
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useLocationsStore, import.meta));
}
